/**
 * screens/modals/ModalHost
 * ──────────────────────────────────────────────────────────────────────────
 * The single place a modal is rendered. It reads the one modal slot on
 * ShellContext and dispatches to the dialog that slot names.
 *
 * WHY THE HOST FETCHES CONTEXT
 * Several dialogs need a fact about the record they act on that the opening
 * click does not carry — the assignment's CURRENCY (so an amount is parsed at
 * the right decimal scale), the reconciliation's consequence sentence, the
 * separation's balance. Rather than thread that through every call site of
 * `openModal` — where it would go stale, or be wrong, or simply be forgotten —
 * the host loads it once from the id.
 *
 * Currency in particular is not optional detail: parsing "340" without knowing
 * whether the currency has two decimals or three is how a spend gets recorded
 * at ten times its value.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, type ReactElement } from 'react';
import { Modal as ShellModal, Loading } from '../../components';
import { useAsyncData } from '../../lib/useAsyncData';
import { fetchAssignmentDetail } from '../../services/cash/cash.service';
import { fetchReconciliations } from '../../services/reconciliation/reconciliation.service';
import { fetchSettlements } from '../../services/settlement/settlement.service';
import { fetchApprovals } from '../../services/approvals/approvals.service';
import { useShell } from '../../context/ShellContext';
import AssignCashModal from './AssignCashModal';
import ApprovalPolicyModal from './ApprovalPolicyModal';
import ApprovalRuleModal from './ApprovalRuleModal';
import CashCountModal from './CashCountModal';
import CategoryModal from './CategoryModal';
import ClosePurposeModal from './ClosePurposeModal';
import OptionValueModal from './OptionValueModal';
import RejectApprovalModal from './RejectApprovalModal';
import ResolveVarianceModal from './ResolveVarianceModal';
import ReverseModal from './ReverseModal';
import SettleModal from './SettleModal';
import TransactionModal from './TransactionModal';
import TransferCustodyModal from './TransferCustodyModal';

export function ModalHost() {
  const { modal } = useShell();
  if (!modal) return null;

  switch (modal.kind) {
    // ── No extra context needed ──────────────────────────────────────────
    case 'assign':
      return <AssignCashModal initialScope={modal.scope} />;
    case 'category':
      return <CategoryModal />;
    case 'approvalPolicy':
      return <ApprovalPolicyModal />;
    case 'approvalRule':
      return <ApprovalRuleModal />;
    case 'optionValue':
      return <OptionValueModal listKind={modal.listKind} />;
    case 'reverse':
      return <ReverseModal txnId={modal.txnId} />;
    case 'transfer':
      return <TransferCustodyModal assignmentId={modal.assignmentId} />;

    // ── Needs the assignment's currency / end rule ───────────────────────
    case 'transaction':
      return (
        <WithAssignment assignmentId={modal.assignmentId}>
          {(detail) => (
            <TransactionModal
              assignmentId={modal.assignmentId}
              txnType={modal.txnType}
              currency={detail.assignment.cur}
            />
          )}
        </WithAssignment>
      );
    case 'count':
      return (
        <WithAssignment assignmentId={modal.assignmentId}>
          {(detail) => (
            <CashCountModal
              assignmentId={modal.assignmentId}
              currency={detail.assignment.cur}
              // A collection assignment reconciles owed → collected →
              // deposited, so that is the count it should open on.
              defaultKind={detail.assignment.scope === 'collection' ? 'collection' : 'limit'}
            />
          )}
        </WithAssignment>
      );
    case 'closePurpose':
      return (
        <WithAssignment assignmentId={modal.assignmentId}>
          {(detail) => (
            <ClosePurposeModal
              assignmentId={modal.assignmentId}
              endRule={detail.assignment.end_rule}
              purposeLabel={detail.assignment.purpose_label}
            />
          )}
        </WithAssignment>
      );

    // ── Needs the record it resolves ─────────────────────────────────────
    case 'resolveVariance':
      return <VarianceLoader reconId={modal.reconId} action={modal.action} />;
    case 'settle':
      return <SettlementLoader separationId={modal.separationId} />;
    case 'reject':
      return <RejectLoader approvalId={modal.approvalId} />;

    default:
      return null;
  }
}

/* ── Context loaders ─────────────────────────────────────────────────────
 * Each renders a placeholder modal while its one fact is in flight, so the
 * dialog appears immediately and fills in — rather than the click seeming to do
 * nothing for a moment.
 */

function Pending({ title }: { title: string }) {
  const { closeModal } = useShell();
  return (
    <ShellModal visible title={title} onClose={closeModal}>
      <Loading />
    </ShellModal>
  );
}

function WithAssignment({
  assignmentId,
  children,
}: {
  assignmentId: string;
  children: (detail: Awaited<ReturnType<typeof fetchAssignmentDetail>>) => ReactElement;
}) {
  const load = useCallback(() => fetchAssignmentDetail(assignmentId), [assignmentId]);
  const { data } = useAsyncData(load, [assignmentId], {
    errorTitle: 'Could not open that assignment',
  });
  if (!data) return <Pending title="Loading" />;
  return children(data);
}

function VarianceLoader({ reconId, action }: { reconId: string; action: 'route' | 'waive' }) {
  const load = useCallback(() => fetchReconciliations(), []);
  const { data } = useAsyncData(load, [], { errorTitle: 'Could not open that count' });
  const recon = data?.reconciliations.find((row) => row.id === reconId);
  if (!recon) return <Pending title={action === 'waive' ? 'Waive variance' : 'Route variance'} />;
  return (
    <ResolveVarianceModal reconId={reconId} action={action} consequence={recon.consequence} />
  );
}

function SettlementLoader({ separationId }: { separationId: string }) {
  const load = useCallback(() => fetchSettlements(), []);
  const { data } = useAsyncData(load, [], { errorTitle: 'Could not open that settlement' });
  const separation = data?.rows.find((row) => row.id === separationId);
  if (!separation) return <Pending title="Settle" />;
  return (
    <SettleModal
      separationId={separationId}
      holder={separation.holder}
      lastDay={separation.last_day}
      openBalanceMinor={separation.open_balance_minor}
      currency={separation.cur}
    />
  );
}

function RejectLoader({ approvalId }: { approvalId: string }) {
  const load = useCallback(() => fetchApprovals('pending'), []);
  const { data } = useAsyncData(load, [], { errorTitle: 'Could not open that request' });
  const approval = data?.find((row) => row.id === approvalId);
  // The label is only for the hint line, so a missing one is not worth blocking
  // the dialog over — a rejection can proceed without it.
  return <RejectApprovalModal approvalId={approvalId} label={approval?.label ?? null} />;
}

export default ModalHost;
